/**
 * Image Search Constants
 */

import type { ImageSourceOption } from './types';

/** Maximum number of images that can be attached to a single article */
export const MAX_IMAGES_PER_ARTICLE = 5;

/** Number of results per page for stock image searches */
export const IMAGES_PER_PAGE = 12;

/** Minimum search query length to enable search */
export const MIN_QUERY_LENGTH = 2;

/** Maximum search query length */
export const MAX_QUERY_LENGTH = 200;

/** Debounce delay for search input (milliseconds) */
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * Available image sources with metadata for the dropdown
 * 
 * Sources are grouped by category:
 * - AI: Generate new images using AI
 * - Stock: Search existing stock photos
 */
export const IMAGE_SOURCES: ImageSourceOption[] = [
  {
    value: 'pixabay',
    label: 'Pixabay',
    category: 'stock',
    description: 'Search free stock photos from Pixabay',
    paginated: true,
  },
  {
    value: 'pexels',
    label: 'Pexels',
    category: 'stock',
    description: 'Search free stock photos from Pexels',
    paginated: true,
  },
  {
    value: 'fal_ai',
    label: 'Fal.ai (AI)',
    category: 'ai',
    description: 'Generate a unique image using AI',
    paginated: false,
  },
];

/** Get image source option by value */
export const getImageSource = (value: string): ImageSourceOption | undefined => {
  return IMAGE_SOURCES.find(source => source.value === value);
};

/** Check if a source is AI-based */
export const isAISource = (value: string): boolean => {
  const source = getImageSource(value);
  return source?.category === 'ai';
};

/** Check if any source in the array is AI-based */
export const hasAnyAISource = (sources: string[]): boolean => {
  return sources.some(s => isAISource(s));
};

/** Check if any source in the array is stock-based */
export const hasAnyStockSource = (sources: string[]): boolean => {
  return sources.some(s => !isAISource(s));
};

/** Get only the stock sources from an array of sources */
export const getStockSourceValues = (): string[] => {
  return IMAGE_SOURCES.filter(s => s.category === 'stock').map(s => s.value);
};

/** Get only the AI sources from an array of sources */
export const getAISourceValues = (): string[] => {
  return IMAGE_SOURCES.filter(s => s.category === 'ai').map(s => s.value);
};

/**
 * Convert selected frontend sources to the backend API `source` parameter.
 * - Both pixabay and pexels selected → 'both'
 * - Only pixabay → 'pixabay'
 * - Only pexels → 'pexels'
 */
export const toApiStockSource = (sources: string[]): 'pixabay' | 'pexels' | 'both' => {
  const stock = sources.filter(s => !isAISource(s));
  const hasPixabay = stock.includes('pixabay');
  const hasPexels = stock.includes('pexels');
  if (hasPixabay && hasPexels) return 'both';
  if (hasPixabay) return 'pixabay';
  if (hasPexels) return 'pexels';
  return 'both'; // fallback
};

/** Check if a source returns paginated results */
export const isPaginatedSource = (value: string): boolean => {
  const source = getImageSource(value);
  return source?.paginated ?? false;
};

/** Dark theme colors matching DistributionHub */
export const THEME_COLORS = {
  background: '#0a0a0a',
  cardBackground: '#1a1a1a',
  border: '#27272a',
  borderHover: '#3f3f46',
  accent: '#60a5fa',
  accentHover: '#3b82f6',
  textPrimary: '#fafafa',
  textSecondary: '#a1a1aa',
  textMuted: '#71717a',
  error: '#ef4444',
  warning: '#f59e0b',
  success: '#22c55e',
} as const;

